import { useState } from 'react';
import { BottomSheet } from '../base/BottomSheet';
import { showToast } from '../base/Toast';
import { useAuth } from '../../hooks/useAuth';
import { ApiError } from '../../api/client';

type Section = 'security' | 'notifications' | 'preferences';

interface AccountSettingsSheetProps {
  open: boolean;
  section: Section;
  onClose: () => void;
}

const SECTION_TITLES: Record<Section, string> = {
  security: 'Bảo mật',
  notifications: 'Cài đặt thông báo',
  preferences: 'Hiển thị',
};

export function AccountSettingsSheet({ open, section, onClose }: AccountSettingsSheetProps) {
  return (
    <BottomSheet open={open} onClose={onClose} title={SECTION_TITLES[section]}>
      {section === 'security' && (
        <div className="space-y-5">
          <SecuritySection />
          <ZaloLinkSection />
        </div>
      )}
      {section === 'notifications' && <NotificationsSection />}
      {section === 'preferences' && <PreferencesSection />}
    </BottomSheet>
  );
}

function SecuritySection() {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (newPassword.length < 8) {
      setError('Mật khẩu mới cần tối thiểu 8 ký tự');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      showToast('Đã đổi mật khẩu thành công', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Đổi mật khẩu thất bại, vui lòng thử lại');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Mật khẩu hiện tại</label>
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Mật khẩu mới (tối thiểu 8 ký tự)</label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Nhập lại mật khẩu mới</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !currentPassword || !newPassword || !confirmPassword}
        className="w-full h-12 rounded-xl bg-agrida-700 text-white text-sm font-semibold disabled:opacity-50"
      >
        {submitting ? 'Đang lưu...' : 'Đổi mật khẩu'}
      </button>
    </div>
  );
}

/**
 * Attach or detach Zalo sign-in for this account.
 *
 * This is the only place linking can happen. The Zalo sign-up flow used to link
 * whatever phone number was typed into its form, which meant knowing someone's
 * number was enough to take over their account — see ZaloAuthService. Here the
 * account comes from the session, so consent proves the Zalo side and the existing
 * login proves the Agrida side.
 */
function ZaloLinkSection() {
  const { user, getZaloLinkUrl, unlinkZalo } = useAuth();
  const [busy, setBusy] = useState(false);
  const linked = Boolean(user?.zaloLinked);

  const handleLink = async () => {
    setBusy(true);
    try {
      // Full redirect out to Zalo; it returns to /dang-nhap, which recognises the
      // link handshake and comes back here.
      window.location.href = await getZaloLinkUrl();
    } catch (e) {
      setBusy(false);
      showToast(e instanceof ApiError ? e.message : 'Không mở được liên kết Zalo', 'error');
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await unlinkZalo();
      showToast('Đã gỡ liên kết Zalo', 'success');
    } catch (e) {
      // The server refuses when Zalo is the account's only way in (no password
      // set), and says so — surface that rather than a generic failure.
      showToast(e instanceof ApiError ? e.message : 'Không gỡ được liên kết Zalo', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-slate-100 pt-4">
      <p className="text-xs font-medium text-slate-600 mb-1">Đăng nhập bằng Zalo</p>
      <p className="text-xs text-slate-500 mb-3">
        {linked
          ? 'Tài khoản này đã liên kết Zalo. Bạn có thể đăng nhập bằng Zalo.'
          : 'Liên kết để đăng nhập nhanh bằng Zalo mà không cần nhập mật khẩu.'}
      </p>
      <button
        type="button"
        onClick={linked ? handleUnlink : handleLink}
        disabled={busy}
        className={`w-full h-12 rounded-xl text-sm font-semibold disabled:opacity-50 ${
          linked ? 'border border-slate-200 text-slate-700' : 'bg-[#0068FF] text-white'
        }`}
      >
        {busy ? 'Đang xử lý...' : linked ? 'Gỡ liên kết Zalo' : 'Liên kết với Zalo'}
      </button>
    </div>
  );
}

function NotificationsSection() {
  const { user, updateProfile } = useAuth();
  const prefs = user?.notificationPrefs;
  const [saving, setSaving] = useState<string | null>(null);

  const toggle = async (key: 'loanUpdates' | 'debtReminders' | 'harvestPrice') => {
    const nextValue = !(prefs?.[key] ?? true);
    setSaving(key);
    try {
      await updateProfile({ notificationPrefs: { [key]: nextValue } });
    } catch {
      showToast('Không lưu được thay đổi, vui lòng thử lại', 'error');
    } finally {
      setSaving(null);
    }
  };

  const rows: Array<{ key: 'loanUpdates' | 'debtReminders' | 'harvestPrice'; label: string; description: string }> = [
    { key: 'loanUpdates', label: 'Cập nhật khoản vay', description: 'Duyệt, từ chối, giải ngân khoản vay' },
    { key: 'debtReminders', label: 'Nhắc nợ đến hạn', description: 'Nhắc lịch trả nợ sắp đến hạn' },
    { key: 'harvestPrice', label: 'Giá thu mua', description: 'Cập nhật giá thu mua nông sản' },
  ];

  return (
    <div className="space-y-1">
      {rows.map((row) => {
        const active = prefs?.[row.key] ?? true;
        return (
          <div key={row.key} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
            <div className="pr-3">
              <p className="text-sm font-medium text-slate-900">{row.label}</p>
              <p className="text-xs text-slate-500">{row.description}</p>
            </div>
            <button
              type="button"
              disabled={saving === row.key}
              onClick={() => toggle(row.key)}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${active ? 'bg-agrida-600' : 'bg-slate-300'} disabled:opacity-50`}
              aria-pressed={active}
              aria-label={row.label}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

const FONT_SIZE_LABELS: Record<0 | 1 | 2, string> = { 0: 'Nhỏ', 1: 'Vừa', 2: 'Lớn' };

function PreferencesSection() {
  const { user, updateProfile } = useAuth();
  const [saving, setSaving] = useState(false);

  const setFontSize = async (size: 0 | 1 | 2) => {
    setSaving(true);
    try {
      await updateProfile({ fontSize: size });
    } catch {
      showToast('Không lưu được thay đổi, vui lòng thử lại', 'error');
    } finally {
      setSaving(false);
    }
  };

  const setLanguage = async (language: 'vi' | 'en') => {
    setSaving(true);
    try {
      await updateProfile({ language });
      showToast('Đã lưu ngôn ngữ. Bản dịch giao diện đầy đủ sẽ có ở phiên bản sau.', 'info');
    } catch {
      showToast('Không lưu được thay đổi, vui lòng thử lại', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium text-slate-900 mb-2">Cỡ chữ</p>
        <div className="grid grid-cols-3 gap-2">
          {([0, 1, 2] as const).map((size) => (
            <button
              key={size}
              type="button"
              disabled={saving}
              onClick={() => setFontSize(size)}
              className={`h-11 rounded-xl border-2 text-sm font-semibold disabled:opacity-50 ${
                (user?.fontSize ?? 1) === size ? 'border-agrida-600 bg-agrida-50 text-agrida-700' : 'border-slate-200 text-slate-600'
              }`}
              style={{ fontSize: size === 0 ? '13px' : size === 2 ? '17px' : '15px' }}
            >
              {FONT_SIZE_LABELS[size]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-slate-900 mb-2">Ngôn ngữ</p>
        <div className="grid grid-cols-2 gap-2">
          {(['vi', 'en'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              disabled={saving}
              onClick={() => setLanguage(lang)}
              className={`h-11 rounded-xl border-2 text-sm font-semibold disabled:opacity-50 ${
                (user?.language ?? 'vi') === lang ? 'border-agrida-600 bg-agrida-50 text-agrida-700' : 'border-slate-200 text-slate-600'
              }`}
            >
              {lang === 'vi' ? 'Tiếng Việt' : 'English'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
